/**
 * FirmyWorkspace — Phase 10.5 company administration.
 *
 * Lives under `/platforma` (default tab, see PlatformaPage). Admin-only —
 * gated identically to UzivateleWorkspace.
 *
 * Toolband (search by Název / IČO / DIČ) + bordered `DataTable`
 * (Název · IČO · DIČ · město · 7-dot `moduly` strip). Row click
 * opens `CompanyFormModal` in edit mode; the form holds the multi-section
 * company shape (identifikace · adresa · banka · moduly · LMS).
 * Delete surfaces 409 blocking-deps in `CompanyDeleteModal` (force-delete
 * with type-the-name confirmation).
 */

import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { SearchInput } from '@/components/SearchInput';
import { Skeleton } from '@/components/Skeleton';
import { Toolband } from '@/components/Toolband';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { useCompaniesListQuery, useSkupinyQuery, type MgmtCompany } from './api';
import { CompanyFormModal } from './CompanyFormModal';
import { SkupinyModal } from './SkupinyModal';

interface FirmyWorkspaceProps {
  isAdmin: boolean;
}

const MODUL_KEYS = [
  { key: 'kontakty' as const, label: 'Kontakty' },
  { key: 'vozidla' as const, label: 'Vozidla' },
  { key: 'ojeta_vozidla' as const, label: 'Ojetá' },
  { key: 'leasing' as const, label: 'Leasing' },
  { key: 'servis' as const, label: 'Servis' },
  { key: 'uskladneni' as const, label: 'Uskladnění' },
  { key: 'posta' as const, label: 'Pošta' },
  { key: 'kalendar' as const, label: 'Kalendář' },
  { key: 'ukoly' as const, label: 'Úkoly' },
  { key: 'pujcovna' as const, label: 'Půjčovna' },
];

export function FirmyWorkspace({ isAdmin }: FirmyWorkspaceProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [modal, setModal] = useState<
    { mode: 'create' } | { mode: 'edit'; company: MgmtCompany } | null
  >(null);
  const [skupinyOpen, setSkupinyOpen] = useState(false);

  // Growable page — 200 silently truncated at multi-dealership scale.
  const [listLimit, setListLimit] = useState(200);
  useEffect(() => setListLimit(200), [debouncedSearch]);
  const list = useCompaniesListQuery(
    { search: debouncedSearch.trim() || undefined, limit: listLimit },
    isAdmin,
  );
  const skupiny = useSkupinyQuery(isAdmin);
  const skupinaNazev = useMemo(
    () => new Map((skupiny.data?.skupiny ?? []).map((s) => [s.id, s.nazev])),
    [skupiny.data],
  );
  const companies = list.data?.companies ?? [];
  const listTotal = list.data?.total ?? companies.length;

  const cols = useMemo<DataTableColumn<MgmtCompany>[]>(
    () => [
      {
        id: 'nazev',
        priority: 'primary',
        header: 'Název',
        cell: (c) => <span className="truncate text-ink">{c.Nazev || '—'}</span>,
        width: 'minmax(0, 1.4fr)',
      },
      {
        id: 'ico',
        priority: 'meta',
        header: 'IČO',
        cell: (c) => (
          <span className="font-mono text-[12px] tabular-nums text-ink-2">
            {c.ICO ? c.ICO : '—'}
          </span>
        ),
        width: '110px',
      },
      {
        id: 'dic',
        priority: 'meta',
        header: 'DIČ',
        cell: (c) => (
          <span className="font-mono text-[12px] tabular-nums text-ink-2">{c.DIC || '—'}</span>
        ),
        width: '140px',
      },
      {
        id: 'mesto',
        priority: 'secondary',
        header: 'Město',
        cell: (c) => <span className="truncate text-ink-2">{c.mesto || '—'}</span>,
        width: 'minmax(0, 1fr)',
      },
      {
        id: 'skupina',
        priority: 'secondary',
        header: 'Skupina',
        cell: (c) => (
          <span className="truncate text-ink-2" data-testid={`firma-skupina-${c.id_firmy}`}>
            {c.id_skupiny != null ? (skupinaNazev.get(c.id_skupiny) ?? `#${c.id_skupiny}`) : '—'}
          </span>
        ),
        width: 'minmax(0, 1fr)',
      },
      {
        id: 'flags',
        priority: 'meta',
        header: 'Moduly',
        cell: (c) => (
          <span
            className="inline-flex items-center gap-1"
            aria-label={moduleFlagsAriaLabel(c)}
          >
            {MODUL_KEYS.map(({ key, label }) => (
              <span
                key={key}
                title={`${label}: ${c.moduly.includes(key) ? 'aktivní' : 'neaktivní'}`}
                aria-hidden
                className={
                  'inline-block size-2 ' + (c.moduly.includes(key) ? 'bg-accent' : 'bg-line-2')
                }
              />
            ))}
          </span>
        ),
        width: '120px',
      },
      {
        id: 'action',
        header: '',
        cell: () => <Icon name="edit" size={14} className="text-ink-3 group-hover:text-ink" />,
        width: '40px',
        align: 'center',
      },
    ],
    [skupinaNazev],
  );

  if (!isAdmin) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">
          <EmptyState
            eyebrow="403"
            title="Správa firem je dostupná jen administrátorům."
            description="Pro přístup kontaktuj správce firmy."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Toolband>
        <SearchInput
          wrapperClassName="min-w-[280px]"
          kbd={null}
          placeholder="Hledat název, IČO, DIČ…"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />

        <Toolband.Spacer />

        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          {list.isLoading ? '—' : `${companies.length} firem`}
        </span>
        <Button
          variant="secondary"
          size="sm"
          icon={<Icon name="users" size={12} />}
          data-testid="firmy-skupiny"
          onClick={() => setSkupinyOpen(true)}
        >
          Skupiny
        </Button>
        <Button
          variant="primary"
          size="sm"
          icon={<Icon name="plus" size={12} />}
          onClick={() => setModal({ mode: 'create' })}
        >
          Nová firma
        </Button>
      </Toolband>

      <div
        data-testid="management-firmy"
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5"
      >
        {list.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : companies.length === 0 ? (
          <EmptyState
            eyebrow="Prázdné"
            title={
              debouncedSearch.trim()
                ? 'Žádná firma neodpovídá hledání.'
                : 'Zatím žádné firmy.'
            }
            description={
              debouncedSearch.trim()
                ? 'Zkuste jiný výraz nebo vyčistěte vyhledávání.'
                : 'Vytvořte první firmu tlačítkem „Nová firma“ vpravo nahoře.'
            }
          />
        ) : (
          <DataTable<MgmtCompany>
            aria-label="Firmy"
            variant="bordered"
            columns={cols}
            data={companies}
            getRowId={(c) => String(c.id_firmy)}
            onRowClick={(c) => setModal({ mode: 'edit', company: c })}
          />
        )}
        {listTotal > companies.length && (
          <div className="flex items-center justify-center gap-3 py-3 font-mono text-[11px] text-ink-3">
            <span>
              Zobrazeno {companies.length} z {listTotal} firem
            </span>
            <Button size="sm" variant="secondary" onClick={() => setListLimit((l) => l + 200)}>
              Načíst další
            </Button>
          </div>
        )}
      </div>

      <SkupinyModal open={skupinyOpen} onOpenChange={setSkupinyOpen} />
      <CompanyFormModal
        open={modal != null}
        mode={modal?.mode ?? 'create'}
        company={modal?.mode === 'edit' ? modal.company : undefined}
        onOpenChange={(open) => {
          if (!open) setModal(null);
        }}
      />
    </div>
  );
}

function moduleFlagsAriaLabel(c: MgmtCompany): string {
  const active = MODUL_KEYS.filter(({ key }) => c.moduly.includes(key)).map(({ label }) => label);
  return active.length === 0 ? 'Žádné aktivní moduly' : `Aktivní moduly: ${active.join(', ')}`;
}
